import { useEffect, useState } from "react";
import { PhoneCall, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STAFFING_LEVELS = [
  "Not sure — advise us",
  "1 first aider",
  "2 first aiders / medics",
  "Ambulance + crew",
  "Paramedic cover",
  "Ambulance + paramedic team",
  "Multi-medics + vehicle (large event)",
];

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";
const labelCls = "text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1";

export default function CallCapture({ open, onOpenChange }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [callerQuery, setCallerQuery] = useState("");
  const [callerName, setCallerName] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventEndDate, setEventEndDate] = useState("");
  const [eventStart, setEventStart] = useState("");
  const [eventFinish, setEventFinish] = useState("");
  const [staffing, setStaffing] = useState("");
  const [eventVenue, setEventVenue] = useState("");
  const [questions, setQuestions] = useState("");
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch((e) => console.debug("Call capture client list failed:", e?.message || e));
    }
  }, [open]);

  const reset = () => {
    setClientId(""); setIsNew(false); setNewName(""); setNewPhone(""); setNewEmail("");
    setCallerQuery(""); setCallerName("");
    setNotes(""); setEventName(""); setEventDate(""); setEventEndDate(""); setEventStart(""); setEventFinish("");
    setStaffing(""); setEventVenue(""); setQuestions(""); setJobs([]);
  };

  const callerMatches = (() => {
    const q = callerQuery.trim().toLowerCase();
    const out = [];
    for (const c of clients) {
      if (!q || c.name.toLowerCase().includes(q)) {
        out.push({ key: `c-${c.id}`, clientId: c.id, person: "", label: c.name, sub: c.email || c.phone || "" });
      }
      for (const ct of c.contacts || []) {
        if (q && ct.name && ct.name.toLowerCase().includes(q)) {
          out.push({ key: `p-${ct.contact_id}`, clientId: c.id, person: ct.name, label: `${ct.name} — ${c.name}`, sub: ct.role || ct.email || "" });
        }
      }
    }
    return out.slice(0, 12);
  })();

  const sendIntakeLink = async () => {
    try {
      const { data } = await api.post("/send-intake-link", { email: newEmail.trim(), name: newName.trim(), origin: window.location.origin });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/call-log", {
        client_id: isNew ? "" : clientId,
        caller_name: callerName,
        new_client_name: isNew ? newName : "",
        new_client_phone: isNew ? newPhone : "",
        new_client_email: isNew ? newEmail : "",
        notes, questions,
        event_name: eventName, event_date: eventDate, event_end_date: eventEndDate,
        event_start: eventStart, event_finish: eventFinish, staffing_level: staffing,
        event_venue: eventVenue,
        jobs: jobs.filter((j) => j.title.trim()),
      });
      toast.success(data.message);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const ready = isNew ? newName.trim() : clientId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="call-capture-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-[#007F3B]" /> On the phone
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <span className={labelCls}>Who's called?</span>
            {!isNew ? (
              <div className="space-y-2">
                {clientId ? (
                  <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5">
                    <span className="text-sm font-bold text-[#0F2547] flex-1 truncate" data-testid="call-selected-client">
                      {clients.find((c) => c.id === clientId)?.name}{callerName ? ` — ${callerName}` : ""}
                    </span>
                    <button type="button" onClick={() => { setClientId(""); setCallerName(""); setCallerQuery(""); }}
                      data-testid="call-clear-client" className="text-slate-400 hover:text-[#D5281B] transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input placeholder="Search a company or a person's name — or pick from the list below…" value={callerQuery} onChange={(e) => setCallerQuery(e.target.value)}
                      data-testid="call-client-search" className={inputCls} />
                    <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-50 max-h-44 overflow-y-auto" data-testid="call-client-results">
                      {callerMatches.length === 0 ? (
                        <p className="text-xs text-slate-400 px-3 py-3">No matches — add them as a new client below.</p>
                      ) : callerMatches.map((m) => (
                        <button key={m.key} type="button"
                          onClick={() => { setClientId(m.clientId); setCallerName(m.person); setCallerQuery(""); }}
                          data-testid={`call-pick-${m.key}`}
                          className="w-full text-left px-3 py-2.5 hover:bg-sky-50 transition-colors">
                          <span className="block text-sm font-semibold text-[#0F2547]">{m.label}</span>
                          {m.sub && <span className="block text-[11px] text-slate-400">{m.sub}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {clientId && !callerName && (clients.find((c) => c.id === clientId)?.contacts || []).length > 0 && (
                  <select value="" onChange={(e) => setCallerName(e.target.value)} data-testid="call-person-select" className={inputCls}>
                    <option value="">Who called? (optional)…</option>
                    {(clients.find((c) => c.id === clientId)?.contacts || []).map((ct) => (
                      <option key={ct.contact_id} value={ct.name}>{ct.name}{ct.role ? ` — ${ct.role}` : ""}</option>
                    ))}
                  </select>
                )}
                <button type="button" onClick={() => setIsNew(true)} data-testid="call-new-client-btn"
                  className="h-10 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] transition-colors">
                  New client
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_150px_auto] gap-2">
                  <input placeholder="New client / company name" value={newName} onChange={(e) => setNewName(e.target.value)}
                    data-testid="call-new-name" className={inputCls} />
                  <input type="tel" placeholder="Phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                    data-testid="call-new-phone" className={inputCls} />
                  <button type="button" onClick={() => setIsNew(false)} data-testid="call-existing-btn"
                    className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#D5281B] transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <input type="email" placeholder="Their email — to send the details form" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                    data-testid="call-new-email" className={inputCls} />
                  <button type="button" onClick={sendIntakeLink} disabled={!newEmail.trim()} data-testid="call-send-intake"
                    className="shrink-0 h-11 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-xs font-bold transition-colors">
                    Send intake link
                  </button>
                </div>
                <p className="text-[11px] text-slate-400">Emails them the new-client form so they fill in their own details, contacts & event info.</p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="bg-[#F4F7FA] rounded-xl border border-slate-200 p-3 space-y-2">
                <span className={labelCls}>Event they want to book</span>
                <input placeholder="Event name" value={eventName} onChange={(e) => setEventName(e.target.value)}
                  data-testid="call-event-name" className={inputCls} />
                <input placeholder="Venue / location" value={eventVenue} onChange={(e) => setEventVenue(e.target.value)}
                  data-testid="call-event-venue" className={inputCls} />
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[10px] text-slate-400">Start date</span>
                    <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                      data-testid="call-event-date" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-slate-400">Finish date (if multi-day)</span>
                    <input type="date" value={eventEndDate} onChange={(e) => setEventEndDate(e.target.value)}
                      data-testid="call-event-end-date" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-slate-400">Start time</span>
                    <input type="time" value={eventStart} onChange={(e) => setEventStart(e.target.value)}
                      data-testid="call-event-start" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-slate-400">Finish time</span>
                    <input type="time" value={eventFinish} onChange={(e) => setEventFinish(e.target.value)}
                      data-testid="call-event-finish" className={inputCls} />
                  </label>
                </div>
                <select value={staffing} onChange={(e) => setStaffing(e.target.value)}
                  data-testid="call-staffing-level" className={inputCls}>
                  <option value="">Staffing level needed…</option>
                  {STAFFING_LEVELS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <span className={labelCls}>Questions to ask them</span>
                <textarea rows={2} value={questions} onChange={(e) => setQuestions(e.target.value)}
                  placeholder="e.g. expected attendance, access times, first aid cover needed…"
                  data-testid="call-questions"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
              </div>
              <div>
                <span className={labelCls}>Call notes — anything you've told them</span>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="What was said, prices mentioned, what you promised to send…"
                  data-testid="call-notes"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white" />
              </div>
            </div>

            <div className="bg-[#F4F7FA] rounded-xl border border-slate-200 p-3">
              <span className={labelCls}>Jobs to do from this call</span>
              <div className="space-y-2">
                {jobs.map((j, i) => (
                  <div key={i} className="flex gap-2" data-testid={`call-job-${i}`}>
                    <input placeholder="e.g. Send quote, ring back Tuesday…" value={j.title}
                      onChange={(e) => setJobs((js) => js.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))}
                      data-testid={`call-job-title-${i}`} className={inputCls} />
                    <input type="date" value={j.due_date}
                      onChange={(e) => setJobs((js) => js.map((x, xi) => xi === i ? { ...x, due_date: e.target.value } : x))}
                      data-testid={`call-job-date-${i}`} className="w-[130px] h-11 rounded-lg border border-slate-300 px-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                    <button type="button" onClick={() => setJobs((js) => js.filter((_, xi) => xi !== i))}
                      data-testid={`call-job-del-${i}`}
                      className="h-11 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-[#D5281B] transition-colors shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setJobs((js) => [...js, { title: "", due_date: "" }])}
                  data-testid="call-job-add"
                  className="w-full h-11 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center justify-center gap-2">
                  <Plus className="h-4 w-4" /> Add a job
                </button>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Jobs land in your To-Do list; an event above creates a quote/booking job too.</p>
            </div>
          </div>

          <button onClick={save} disabled={saving || !ready} data-testid="call-save"
            className="w-full h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 transition-colors text-white text-sm font-bold">
            {saving ? "Saving…" : "Save call"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
